import { OvFormProvider, OvTabList, PageWrapper, OvFormContext } from '@components'
import { CLIENT_DEFAULT_VALUES, CLIENT_DETAIL_TABS, LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Save } from '@mui/icons-material'
import { TabContext, TabPanel } from '@mui/lab'
import { Box, Button, Tab, Tooltip } from '@mui/material'
import { IOVErrorResponseData, ISingleClient } from '@typings'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues, useForm, UseFormReturn, useWatch } from 'react-hook-form'
import { generatePath, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { clientValidationSchema } from 'src/partials/forms/client/validation/client.form.validation'
const ClientCreateContent = observer(({ form }: { form: UseFormReturn<any> }) => {
  const { client, shared, ui, company } = useStore()
  const { setAllowNavigation, allowNavigation, allowNavigationRef } = useContext(OvFormContext)
  const isMobile = useIsMobile()
  const [selectedTab, setSelectedTab] = useState(CLIENT_DETAIL_TABS.CREATE[0].value)
  const pendingNavigationRef = useRef<string | null>(null)

  const navigate = useNavigate()

  const handleTabChange = (e: React.SyntheticEvent<Element, Event>, val: string) => setSelectedTab(val)

  const clientName = useWatch({ name: 'name', control: form.control })

  const onSubmitCallBack = (data: FieldValues) => {
    data.country_id = data.country.id
    data.company_id = company.current_company_id?.toString() || ''
    client
      .create(data as ISingleClient)
      .then(res => handleSucces(res))
      .catch((e: IOVErrorResponseData) => {
        handleErrors(e.errors, e.message)
      })
  }

  const handleBackAction = () => {
    // Let the navigation blocker handle the confirmation
    navigate(URL_PATHS.CLIENT_LIST)
  }

  const handleSucces = (data: ISingleClient) => {
    const route = generatePath(URL_PATHS.CLIENT_DETAIL, { id: data.id })
    toast('Klantgegevens opggeslagen', { type: 'success' })

    // Set ref DIRECTLY and IMMEDIATELY - no waiting for state updates
    if (allowNavigationRef) {
      allowNavigationRef.current = true
    }

    // Store the route and trigger navigation
    pendingNavigationRef.current = route
    setAllowNavigation?.(true)
  }

  // Navigate when allowNavigation becomes true
  useEffect(() => {
    if (allowNavigation && pendingNavigationRef.current) {
      const route = pendingNavigationRef.current
      pendingNavigationRef.current = null
      navigate(route)
    }
  }, [allowNavigation, navigate])

  const handleErrors = (errors: Record<string, string[]>, message?: string) => {
    form.clearErrors()
    const errorKeys = Object.keys(errors)

    errorKeys.map(singleKey =>
      form.setError(singleKey as keyof typeof CLIENT_DEFAULT_VALUES, {
        type: 'custom',
        message: errors[singleKey][0],
      })
    )

    if (message) toast(message, { type: 'error' })
  }

  const handleSubmit = () => {
    form.handleSubmit(
      onSubmitCallBack,
      (errors) => {
        toast('Vul alle verplichte velden in', { type: 'error' })
      }
    )()
  }

  const getPageTitle = useMemo(() => (clientName !== '' ? clientName : LABELS.CLIENT_CREATE), [clientName])

  const renderClientTabs = useMemo(
    () => (
      <TabContext value={selectedTab}>
        <OvTabList onChange={handleTabChange}>
          {CLIENT_DETAIL_TABS.CREATE.map(singleTab => (
            <Tab
              key={singleTab.value}
              value={singleTab.value}
              label={singleTab.label}
            />
          ))}
        </OvTabList>
        <Box>
          {CLIENT_DETAIL_TABS.CREATE.map(singleTab => (
            <TabPanel
              key={singleTab.value}
              sx={{ padding: '24px 0' }}
              value={singleTab.value}>
              {singleTab.component}
            </TabPanel>
          ))}
        </Box>
      </TabContext>
    ),
    [selectedTab, client.current_client, client.single, client.loading]
  )

  useEffect(() => {
    shared.getCountries().then(() => {
      // Set Netherlands as default country if not already set
      const currentCountry = form.getValues('country')
      if (!currentCountry || !currentCountry.id) {
        const netherlands = shared.countries.find(country => country.code === 'NL')
        if (netherlands) {
          form.setValue('country', netherlands, { shouldValidate: true })
        }
      }
    })
  }, [])

  return (
    <PageWrapper
      pageTitle={getPageTitle}
      backAction={handleBackAction}
      actions={
        <Box
          display="flex"
          gap={1}
          width={isMobile ? '100%' : 'auto'}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleBackAction}
            sx={{ flex: isMobile ? 1 : 'unset' }}>
            {LABELS.CANCEL}
          </Button>
          <Tooltip title={TOOLTIPS.SAVE_CLIENT}>
            <Button
              size="small"
              endIcon={<Save />}
              variant="contained"
              onClick={handleSubmit}
              sx={{ flex: isMobile ? 1 : 'unset' }}>
              {LABELS.CLIENT_SAVE}
            </Button>
          </Tooltip>
        </Box>
      }
      fixedActions={true}>
      {renderClientTabs}
    </PageWrapper>
  )
})

export const ClientCreateView = observer(() => {
  const { company } = useStore()

  const form = useForm({
    resolver: yupResolver(clientValidationSchema()),
    mode: 'onChange',
    defaultValues: {
      ...CLIENT_DEFAULT_VALUES,
      company_id: company.current_company_id?.toString() || '',
    },
  })

  const handleSubmit = () => {
    // This will be handled by the inner component
  }

  return (
    <OvFormProvider
      form={form}
      onSubmit={handleSubmit}>
      <ClientCreateContent form={form} />
    </OvFormProvider>
  )
})
